/**
 * Thin wrapper around fetch — not a full library (no react-query/SWR yet;
 * genuinely reasonable to add later for caching/revalidation, just not
 * needed for this first vertical slice). Every call goes through here so
 * the auth header and error handling live in exactly one place.
 *
 * This code runs in the browser, not on the Next.js server, so it needs a
 * URL the BROWSER can actually reach — not the internal Docker network
 * address (api:8000) other backend services use.
 *
 * Deliberately NOT a hardcoded "localhost:8080": confirmed via a real
 * failure (net::ERR_CONNECTION_RESET) that opening the dashboard from a
 * different machine on the LAN sends that browser to ITS OWN localhost,
 * not the server's — nothing is listening there. Matching whatever host
 * the page was actually loaded from instead means this works unchanged
 * whether it's opened as localhost, a LAN IP, or (later) a real domain.
 * NEXT_PUBLIC_API_URL remains available as an explicit override for
 * setups where that guess is wrong (e.g. the API sits behind a different
 * port/subdomain than the dashboard itself, once actually deployed).
 */
export function getApiUrl(): string {
  if (process.env.NEXT_PUBLIC_API_URL) return process.env.NEXT_PUBLIC_API_URL;
  if (typeof window !== "undefined") {
    return `${window.location.protocol}//${window.location.hostname}:8080`;
  }
  return "http://localhost:8080";
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("access_token");
}

export function setToken(token: string | null) {
  if (token) {
    localStorage.setItem("access_token", token);
  } else {
    localStorage.removeItem("access_token");
  }
}

// The station the dashboard is currently working on. Set by the station
// switcher (lib/station-context.tsx); every call about a station's own data
// carries it as ?station=N. Platform-wide calls (accounts, roles, the station
// list, health) don't.
let currentStation: number | null = null;
let currentMount: string | null = null;
export function setCurrentStation(id: number | null, mount: string | null = null) {
  currentStation = id;
  currentMount = mount;
}
const PLATFORM_WIDE = ["/manual", "/auth", "/users", "/roles", "/stations", "/website", "/app-settings", "/health", "/services"];

function withStation(path: string): string {
  if (currentStation === null || PLATFORM_WIDE.some((p) => path === p || path.startsWith(p + "/") || path.startsWith(p + "?"))) {
    return path;
  }
  if (/[?&]station=/.test(path)) return path;
  return `${path}${path.includes("?") ? "&" : "?"}station=${currentStation}`;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  path = withStation(path);
  const token = getToken();
  const headers = new Headers(options.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  // Only default to JSON when the caller hasn't already set a
  // Content-Type of their own. Without the has() check, this
  // unconditionally overwrote login()'s explicit
  // "application/x-www-form-urlencoded" with "application/json" — the
  // body itself is a URLSearchParams string (not FormData, so the
  // isinstance check below didn't skip it), so the browser sent
  // correctly-encoded form data mislabeled as JSON. FastAPI's
  // OAuth2PasswordRequestForm parser couldn't find `username`/`password`
  // in a body it was told to parse as JSON, and rejected it with 422 —
  // confirmed via a real login attempt, after the earlier CORS/hostname
  // fixes had gotten the request through to the API at all.
  if (
    options.body &&
    !(options.body instanceof FormData) &&
    !headers.has("Content-Type")
  ) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(`${getApiUrl()}${path}`, { ...options, headers });

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = await response.json();
      detail = body.detail || detail;
    } catch {
      // response wasn't JSON — keep the plain statusText
    }
    throw new ApiError(response.status, detail);
  }

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return response.json();
  }
  return response.text() as unknown as T;
}

/**
 * Same endpoint as api.uploadTrack (POST /tracks/upload), but built on
 * XMLHttpRequest instead of fetch specifically for xhr.upload.onprogress
 * — fetch has no equivalent upload-progress event in any browser today,
 * and the batch-upload modal needs a real byte-level progress bar per
 * file, not just a spinner. Returns both the result promise and an
 * abort() so the modal can let someone cancel a single in-flight upload
 * without touching the others in the queue.
 */
export function uploadTrackWithProgress(
  file: File,
  onProgress: (percent: number) => void,
  category = "music"
): { promise: Promise<Track>; abort: () => void } {
  const xhr = new XMLHttpRequest();
  const form = new FormData();
  form.set("file", file);

  const promise = new Promise<Track>((resolve, reject) => {
    xhr.open("POST", `${getApiUrl()}${withStation(`/tracks/upload?category=${encodeURIComponent(category)}`)}`);
    const token = getToken();
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as Track);
        } catch {
          reject(new ApiError(xhr.status, "Upload succeeded but the response couldn't be read."));
        }
        return;
      }
      let detail = xhr.statusText || `Upload failed (${xhr.status})`;
      try {
        const body = JSON.parse(xhr.responseText);
        detail = body.detail || detail;
      } catch {
        // response wasn't JSON — keep the fallback above
      }
      reject(new ApiError(xhr.status, detail));
    };

    xhr.onerror = () => reject(new ApiError(0, "Network error during upload."));
    xhr.onabort = () => reject(new ApiError(0, "Upload cancelled."));

    xhr.send(form);
  });

  return { promise, abort: () => xhr.abort() };
}

export interface Track {
  id: string;
  title: string;
  artist: string;
  genre: string | null;
  album: string | null;
  year: number | null;
  duration_seconds: number | null;
  raw_file_path: string | null;
  file_path: string | null;
  cover_art_path: string | null;
  status: "pending" | "processing" | "ready" | "error";
  error_message: string | null;
  created_at: string;
  // music | jingle | station_id | promo (older API responses had none: music)
  category?: string;
  // The tag and artwork check: what is missing (see ISSUE_INFO).
  issues?: string[];
  tags_missing?: string[] | null;
}

// Problems are worth fixing before a track goes to air; hints are nice to have.
export const ISSUE_INFO: Record<string, { label: string; problem: boolean }> = {
  unreadable_tags: { label: "Tags unreadable", problem: true },
  no_title: { label: "No title tag", problem: true },
  no_artist: { label: "No artist", problem: true },
  no_artwork: { label: "No artwork", problem: true },
  no_album: { label: "No album", problem: false },
  no_year: { label: "No year", problem: false },
  no_genre: { label: "No genre", problem: false },
};

export function trackProblems(t: Track): string[] {
  return (t.issues ?? []).filter((i) => ISSUE_INFO[i]?.problem);
}

export const TRACK_CATEGORIES: { key: string; label: string; plural: string }[] = [
  { key: "music", label: "Music", plural: "Music" },
  { key: "jingle", label: "Jingle", plural: "Jingles" },
  { key: "station_id", label: "Station ID", plural: "Station IDs" },
  { key: "promo", label: "Promo", plural: "Promos" },
];

export function categoryLabel(key: string | undefined): string {
  return TRACK_CATEGORIES.find((c) => c.key === (key ?? "music"))?.label ?? "Music";
}

export interface RotationRule {
  id: string;
  name: string;
  category: string;
  mode: "songs" | "minutes";
  every_n: number;
  enabled: boolean;
  created_at: string;
  pool_size: number;
}

export interface RotationOverview {
  rules: RotationRule[];
  categories: { key: string; label: string; count: number }[];
  max_rules: number;
}

export interface NowPlaying {
  title: string | null;
  artist: string | null;
  started_at: string | null;
  track_id: string | null;
  album: string | null;
  year: number | null;
  duration_seconds: number | null;
  has_cover_art: boolean;
  // True while a real playlist track is playing (computed by the API from
  // the track's start time and duration) — false when nothing is playing,
  // the station is idle/silent, or only the emergency file is playing.
  on_air: boolean;
  // A live DJ is on air (title is "Live broadcast" or what their software sent,
  // artist is the DJ's name).
  live?: boolean;
  dj?: string | null;
}

// Icecast is a separate service the dashboard never proxies through — the
// browser connects to it directly, same as any listener would. Not a
// hardcoded "localhost:8001": a browser on another machine needs the
// actual server's address, not its own localhost (confirmed via a real
// net::ERR_CONNECTION_RESET), so this matches whatever host the page was
// actually loaded from — same reasoning as getApiUrl() above.
// NEXT_PUBLIC_STREAM_URL remains available as an explicit override for
// setups where that guess is wrong (e.g. Icecast ends up on a different
// host/port than the dashboard once deployed for real). Shared by the
// Listen page and the Home dashboard's Now Playing player so there's one
// place this logic lives, not three slightly-different copies.
// The address of the stream server itself (no mount), for links like Icecast's admin page.
export function getStreamOrigin(): string {
  const override = process.env.NEXT_PUBLIC_STREAM_URL;
  if (override) {
    try {
      return new URL(override).origin;
    } catch {
      /* fall through to the guess below */
    }
  }
  if (typeof window !== "undefined") return `${window.location.protocol}//${window.location.hostname}:8001`;
  return "http://localhost:8001";
}

export function getStreamUrl(): string {
  // The current station's own stream address ("/stream", "/80-s", ...).
  const mount = currentMount ?? "/stream";
  const override = process.env.NEXT_PUBLIC_STREAM_URL;
  if (override) {
    // The override names the server (and the first station's stream); another
    // station is on the same server under its own mount.
    if (currentMount === null) return override;
    try {
      const url = new URL(override);
      url.pathname = currentMount;
      return url.toString();
    } catch {
      return override;
    }
  }
  if (typeof window !== "undefined") {
    return `${window.location.protocol}//${window.location.hostname}:8001${mount}`;
  }
  return `http://localhost:8001${mount}`;
}

export interface StationConfig {
  name: string;
  description: string | null;
  url: string | null;
  genre: string | null;
  crossfade_seconds: number;
  fade_seconds: number;
  fallback_enabled: boolean;
  fallback_playlist_id: string | null;
  broadcast_status_style: "standard" | "pill";
  updated_at: string;
}

export interface ManualSummary {
  slug: string;
  title: string;
  summary: string;
}

export interface ManualPage {
  slug: string;
  title: string;
  markdown: string;
}

export interface ManualHit {
  slug: string;
  title: string;
  snippet: string;
  hits: number;
}

export interface GeoRules {
  mode: "off" | "block" | "allow";
  countries: string[];
  allow_unknown: boolean;
}

export interface GeoState extends GeoRules {
  enforced_by_server: boolean;
  geoip: { installed: boolean; database: string | null; updated_at: string | null };
  blocked_24h: { country_code: string | null; country: string; count: number }[];
  blocked_24h_total: number;
}

export interface GeoTestResult {
  allowed: boolean;
  reason: string;
  country_code: string | null;
  country: string | null;
}

export interface StationTweaks {
  fallback_enabled: boolean;
  fallback_playlist_id: string | null;
  broadcast_status_style: "standard" | "pill";
}

export interface StationConfigUpdate {
  name: string;
  description: string | null;
  url: string | null;
  genre: string | null;
  crossfade_seconds: number;
  fade_seconds: number;
}

export interface StreamProfile {
  id: string;
  name: string;
  codec: string;
  bitrate_kbps: number;
  mount: string;
  enabled: boolean;
  created_at: string;
  on_air: boolean | null;
}

export interface StreamProfiles {
  master: { codec: string; bitrate_kbps: number; mount: string; on_air: boolean | null };
  profiles: StreamProfile[];
  codecs: { key: string; label: string; bitrates: number[] }[];
  presets: { name: string; codec: string; bitrate_kbps: number }[];
  max_profiles: number;
}

export interface DjAccount {
  id: string;
  username: string;
  display_name: string;
  enabled: boolean;
  created_at: string;
  last_live_at: string | null;
  is_live: boolean;
}

export interface LiveStatus {
  live: { username: string; display_name: string; since: string; title: string | null } | null;
  harbor: { port: number; mount: string };
  accounts: DjAccount[];
}

export interface StationStatus {
  online: boolean;
  listener_count: number | null;
  stream_url: string | null;
  error: string | null;
}

export interface InfraConfig {
  icecast_host: string;
  icecast_port: number;
  icecast_mount: string;
  source_password_is_default: boolean;
  admin_password_is_default: boolean;
  relay_password_is_default: boolean;
}

// Rules of a smart playlist: matching ready music tracks, all rules together.
export interface SmartCriteria {
  genres: string[];
  artist_contains: string | null;
  album_contains: string | null;
  year_from: number | null;
  year_to: number | null;
  added_within_days: number | null;
  min_seconds: number | null;
  max_seconds: number | null;
}

export interface SmartPreview {
  count: number;
  total_seconds: number;
  sample: { title: string; artist: string; year: number | null; genre: string | null }[];
}

export interface SmartOptions {
  genres: { name: string; count: number }[];
  year_min: number | null;
  year_max: number | null;
  total: number;
}

export type RepeatUnit = "days" | "weeks" | "months";

export interface PlaylistSummary {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  shuffle: boolean;
  // Repeat off = play once through, then the station goes silent. On with
  // no value/unit = loop non-stop. On with them = play it again every
  // value unit, counted from when it starts.
  repeat_enabled: boolean;
  repeat_value: number | null;
  repeat_unit: RepeatUnit | null;
  next_run_at: string | null; // recurring only: when it is next due to start
  is_smart: boolean;
  smart_criteria: SmartCriteria | null;
  track_count: number;
  created_at: string;
  updated_at: string;
}

export interface PlaylistTrack {
  id: string; // the playlist_tracks row id, not the track id
  position: number;
  track_id: string;
  title: string;
  artist: string;
  duration_seconds: number | null;
  cover_art_path: string | null;
  status: Track["status"];
}

export interface Playlist {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  shuffle: boolean;
  repeat_enabled: boolean;
  repeat_value: number | null;
  repeat_unit: RepeatUnit | null;
  next_run_at: string | null; // recurring only: when it is next due to start
  is_smart: boolean;
  smart_criteria: SmartCriteria | null;
  created_at: string;
  updated_at: string;
  tracks: PlaylistTrack[];
}

export interface PlaylistSettings {
  shuffle: boolean;
  repeat_enabled: boolean;
  repeat_value: number | null;
  repeat_unit: RepeatUnit | null;
}

export type RecurrenceFrequency = "none" | "daily" | "weekly" | "monthly";

export interface Recurrence {
  frequency: RecurrenceFrequency;
  interval: number;
  weekdays: string[]; // MO..SU
  until: string | null; // YYYY-MM-DD
}

export interface ShowPlaylist {
  playlist_id: string;
  name: string;
  track_count: number;
}

// Times are naive LOCAL ISO strings ("2026-10-12T15:00:00") in the station
// timezone — the same wall-clock time the calendar displays.
export interface Show {
  id: string;
  name: string;
  description: string | null;
  color: string;
  starts_local: string;
  duration_minutes: number;
  timezone: string;
  recurrence: Recurrence;
  playlists: ShowPlaylist[];
  empty: boolean; // none of its playlists has a playable track
  accepts_requests: boolean; // listener requests are taken and aired during this show
}

export interface ShowInput {
  name: string;
  description: string | null;
  color: string;
  starts_local: string;
  duration_minutes: number;
  recurrence: Recurrence;
  playlist_ids: string[];
  accepts_requests: boolean;
}

export interface ShowOccurrence {
  show_id: string;
  name: string;
  color: string;
  start_local: string;
  end_local: string;
  start_utc: string;
  end_utc: string;
  recurring: boolean;
  is_current: boolean;
  playlist_count: number;
  empty: boolean;
}

export interface ScheduleNowNext {
  timezone: string;
  now_local: string;
  current: ShowOccurrence | null;
  next: ShowOccurrence | null;
}

export interface ContainerUsage {
  cpu: number; // like `docker stats`: 100 = one full core busy
  mem: number; // bytes in use
  mem_limit: number; // bytes (0 = unknown)
  net_rx: number; // total bytes received since the container started
  net_tx: number;
}

export interface StatsSample {
  t: number; // unix seconds
  online: boolean;
  listeners: number | null;
  bandwidth_kbps: number | null;
  storage_bytes: number | null;
  storage_total_bytes?: number | null; // size of the disk the storage lives on
  containers: Record<string, ContainerUsage | null>;
}

export interface StatsResponse {
  interval_seconds: number;
  samples: StatsSample[]; // oldest first
}

export interface StorageHistory {
  points: { t: number; bytes: number }[]; // oldest first, one per hour
}

export type RequestStatus = "pending" | "approved" | "queued" | "played" | "rejected" | "expired";

// Phone-manager notifications: which kinds this device wants.
export interface PushPrefs {
  on_request: boolean;
  on_offline: boolean;
}
export interface PushState extends PushPrefs {
  subscribed: boolean;
}

export interface SongRequest {
  id: string;
  track_id: string | null;
  title: string;
  artist: string;
  has_cover_art: boolean;
  requester_name: string | null;
  message: string | null;
  status: RequestStatus;
  flagged: boolean;
  play_next: boolean;
  created_at: string;
  decided_at: string | null;
  decided_by_name: string | null;
  reject_reason: string | null;
  played_at: string | null;
}

export interface RequestList {
  items: SongRequest[];
  next_before: string | null;
}

export interface RequestSummary {
  pending: number;
  approved: number; // approved + already handed to the stream
  played_today: number;
  rejected_today: number;
  received_today: number;
  enabled: boolean;
  accepting: boolean;
  closed_reason: string | null;
}

export interface RequestInsights {
  days: number;
  total: number;
  by_hour: number[]; // 24 buckets, station local time
  by_day: { date: string; count: number }[];
  top: { title: string; artist: string; count: number }[];
}

export interface RequestRules {
  enabled: boolean;
  auto_approve: boolean;
  block_profanity: boolean;
  max_per_listener_per_hour: number;
  repeat_window_minutes: number;
  min_tracks_between: number;
  max_queue: number;
}

export interface RequestPublicConfig {
  accepting: boolean;
  reason: string | null;
  max_per_listener_per_hour: number;
  auto_approve: boolean;
}

export interface RequestSearchResult {
  id: string;
  title: string;
  artist: string;
  album: string | null;
  has_cover_art: boolean;
  duration_seconds: number | null;
  available: boolean;
  available_at: string | null;
}

export interface RequestSubmitted {
  id: string;
  status: RequestStatus;
  title: string;
  artist: string;
  message: string;
}

export interface ActivityItem {
  id: string;
  created_at: string; // ISO, UTC
  category: string; // media, playlist, schedule, settings, user, role, auth, system
  action: string; // e.g. "track.uploaded"
  summary: string;
  actor_name: string | null; // null = nobody (older rows); "System" for automatic events
  target_type: string | null;
  target_id: string | null;
}

export interface ActivityResponse {
  items: ActivityItem[];
  categories: string[]; // the ones this account may see
  next_before: string | null; // pass as `before` for the next older page
}

export interface RecentPlay {
  id: string;
  track_id: string | null;
  title: string;
  artist: string;
  album: string | null;
  started_at: string; // ISO, UTC
  duration_seconds: number | null;
  has_cover_art: boolean;
}

export interface HistoryPoint {
  t: number; // unix seconds: start of the hour, or of the station's day
  listeners_avg: number;
  listeners_peak: number;
  listener_seconds: number;
  bandwidth_avg_kbps: number;
  bandwidth_peak_kbps: number;
  bytes_sent: number;
}

export interface AudienceSlice {
  name: string;
  sessions: number;
  percent: number;
}

export interface AudienceStats {
  days: number;
  tracking_since: string | null;
  sessions: number;
  unique_listeners: number;
  returning_listeners: number;
  new_listeners: number;
  return_rate_percent: number;
  total_listening_hours: number;
  avg_session_seconds: number;
  countries: { code: string; name: string; sessions: number; listeners: number; hours: number; percent: number }[];
  cities: { city: string; country_code: string | null; sessions: number; percent: number }[];
  located_sessions: number;
  private_sessions: number;
  devices: AudienceSlice[];
  operating_systems: AudienceSlice[];
  players: AudienceSlice[];
  peak_hour_local: number | null;
  geoip: { installed: boolean; database: string | null; updated_at: string | null };
}

export interface LiveListener {
  country_code: string | null;
  country: string | null;
  city: string | null;
  player: string | null;
  os: string | null;
  device_type: string | null;
  connected_seconds: number;
}

export interface TodayStats {
  timezone: string;
  since: number; // unix seconds: midnight in the station's timezone
  peak_listeners: number;
  listening_hours: number;
  plays: number;
}

export interface StatsHistory {
  bucket: "hour" | "day";
  points: HistoryPoint[]; // oldest first
  summary: {
    peak_listeners: number;
    avg_listeners: number;
    listening_hours: number;
    bytes_sent: number;
    plays: number;
  };
}

export interface TrackStat {
  track_id: string | null;
  title: string;
  artist: string;
  plays: number;
  avg_listeners: number | null;
  churn_percent: number | null;
  duration_seconds: number | null;
}

export interface TrackStats {
  top: TrackStat[];
  churn: TrackStat[];
  total_plays: number;
}

export interface ServiceStatus {
  name: string;
  display_name: string;
  status: string;
  indicator: "green" | "amber" | "red";
  detail: string | null;
}

export interface ServiceOverview {
  name: string;
  title: string;
  subtitle: string;
  indicator: "green" | "amber" | "red";
  status: string;
  detail: string | null;
  image: string | null;
  started_at: string | null;
  tiles: { key: string; label: string; value: string; percent: number | null }[];
  details: { label: string; value: string }[];
  history: number[];
  history_label: string;
}

export interface ServiceRestartResult {
  name: string;
  restarted: boolean;
  detail: string | null;
}

export interface UpNextTrack {
  id: string;
  // Already started: shown greyed out among a playlist's last tracks
  // when nothing is queued after it.
  greyed?: boolean;
  requested?: boolean; // a listener's request, ahead of the regular tracks
  title: string;
  artist: string;
  duration_seconds: number | null;
  has_cover_art: boolean;
}

export interface CurrentUser {
  email: string;
  role: string; // the role's key
  role_name: string;
  name: string | null;
  // Everything this account may do, e.g. "media.view", "users.manage".
  permissions: string[];
}

// One dashboard account, as managed on the Users page.
export interface UserAccount {
  id: string;
  email: string;
  name: string | null;
  role: string; // the role's key
  role_name: string;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
  // Stations this account works on; accounts with the Stations permission reach all.
  station_ids: number[];
  all_stations: boolean;
}

// A station as the switcher and the Stations page see it.
export interface StationBrief {
  id: number;
  slug: string;
  name: string;
  mount: string;
  genre: string | null;
}

export interface StationOverview extends StationBrief {
  online: boolean;
  on_air: boolean;
  listeners: number | null;
  bandwidth_kbps: number | null;
  title: string | null;
  artist: string | null;
  track_id: string | null;
  has_cover_art: boolean;
}

export interface StationAdmin extends StationBrief {
  description: string | null;
  url: string | null;
  timezone: string;
  staff_count: number;
  track_count: number;
  is_default: boolean;
}

export interface StationCreate {
  name: string;
  slug?: string;
  mount?: string;
  description?: string | null;
  genre?: string | null;
  url?: string | null;
  timezone?: string;
}

export type StationEdit = Partial<Pick<StationAdmin, "name" | "description" | "genre" | "url" | "timezone">>;

// A named set of permissions (Users > Roles).
export interface RoleInfo {
  id: string;
  key: string;
  name: string;
  description: string | null;
  permissions: string[];
  is_system: boolean; // the built-in Admin role: locked, always everything
  user_count: number;
}

// One row of the permission matrix.
export interface PermissionArea {
  key: string;
  label: string;
  view: string; // what "View" allows
  manage: string | null; // what "Manage" allows (null = nothing to manage)
}

export interface RoleCreate {
  name: string;
  description: string | null;
  permissions: string[];
}

export interface RoleUpdate {
  name?: string;
  description?: string | null;
  permissions?: string[];
}

export interface UserCreate {
  email: string;
  password: string;
  name: string | null;
  role: string;
  station_ids?: number[];
}

// Only the fields that are sent change; an empty name clears it.
export interface UserUpdate {
  email?: string;
  password?: string;
  name?: string | null;
  role?: string;
  is_active?: boolean;
  station_ids?: number[];
}

export interface SiteContent {
  site_name: string;
  tagline: string | null;
  about: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  contact_address: string | null;
  socials: Record<string, string>;
}

export interface StationVisibility {
  id: number;
  name: string;
  slug: string;
  genre: string | null;
  public_visible: boolean;
  sort_order: number;
}

export interface WebsiteBlock {
  id: number;
  region: string;
  type: string;
  title: string | null;
  config: Record<string, unknown>;
  pages: string[];
  weight: number;
  enabled: boolean;
}

export interface WebsiteBlockInput {
  region: string;
  type: string;
  title: string | null;
  config: Record<string, unknown>;
  pages: string[];
  enabled: boolean;
}

export interface MenuItemData {
  label: string;
  kind: "home" | "page" | "station" | "url";
  ref: string;
  new_tab: boolean;
  children: MenuItemData[];
}

export interface WebsiteMenu {
  key: string;
  title: string;
  items: MenuItemData[];
}

export interface WebsitePageSummary {
  id: number;
  slug: string;
  title: string;
  published: boolean;
  updated_at: string;
}

export interface WebsitePage extends WebsitePageSummary {
  description: string | null;
  body: string;
  show_title: boolean;
}

export interface WebsitePageInput {
  slug: string;
  title: string;
  description: string | null;
  body: string;
  show_title: boolean;
  published: boolean;
}

export interface WebsiteAsset {
  id: string;
  filename: string;
  content_type: string;
  size: number;
  created_at: string;
}

export interface WebsiteBranding {
  logo_asset_id: string | null;
  favicon_asset_id: string | null;
  theme: Record<string, string>;
}

// The dashboard / phone-manager logo (Settings → Logo & Artwork). The two public reads need no login.
export interface DashboardLogoInfo {
  has_logo: boolean;
  is_default: boolean; // the built-in ROA Radio logo
  version: number; // changes whenever the logo does, used to refresh cached copies
  filename: string | null;
  size: number;
}

export function dashboardLogoUrl(version: number): string {
  return `${getApiUrl()}/public/dashboard-logo?v=${version}`;
}

export async function fetchDashboardLogoInfo(): Promise<DashboardLogoInfo> {
  const res = await fetch(`${getApiUrl()}/public/dashboard-logo/info`);
  if (!res.ok) throw new ApiError(res.status, res.statusText);
  return res.json();
}

export function websiteAssetUrl(id: string): string {
  return `${getApiUrl()}/public/assets/${id}`;
}

// Where the public website lives: same machine as the dashboard, port 3001.
export function publicSiteUrl(): string {
  if (typeof window === "undefined") return "http://localhost:3001";
  return `${window.location.protocol}//${window.location.hostname}:3001`;
}


export type AppStationRow = {
  id: number; slug: string; name: string; app_visible: boolean; sort_order: number; public_visible: boolean;
  note_enabled: boolean; note_text: string; note_tone: "info" | "warning"; note_url: string;
  note_starts_at: string | null; note_ends_at: string | null;
};
export type AppSettings = {
  announcement_enabled: boolean; announcement_text: string; announcement_tone: "info" | "warning";
  maintenance_enabled: boolean; maintenance_message: string;
  show_requests: boolean; show_schedule: boolean; show_listener_count: boolean; show_recently_played: boolean; show_up_next: boolean;
  min_version: string; update_message: string; update_url: string;
  accent_color: string; welcome_text: string; default_station_id: number | null;
  latest_version: string; announcement_url: string;
  announcement_starts_at: string | null; announcement_ends_at: string | null;
  maintenance_starts_at: string | null; maintenance_ends_at: string | null;
  support_email: string; support_website: string; support_social: string;
};
export type AppSettingsState = AppSettings & { stations: AppStationRow[] };

export const api = {
  getBranding: () => request<WebsiteBranding>("/website/branding"),
  saveBranding: (payload: WebsiteBranding) =>
    request<WebsiteBranding>("/website/branding", { method: "PUT", body: JSON.stringify(payload) }),
  listBlocks: () => request<WebsiteBlock[]>("/website/blocks"),
  createBlock: (payload: WebsiteBlockInput) =>
    request<WebsiteBlock>("/website/blocks", { method: "POST", body: JSON.stringify(payload) }),
  updateBlock: (id: number, payload: WebsiteBlockInput) =>
    request<WebsiteBlock>(`/website/blocks/${id}`, { method: "PUT", body: JSON.stringify(payload) }),
  deleteBlock: (id: number) => request<void>(`/website/blocks/${id}`, { method: "DELETE" }),
  orderBlocks: (rows: { id: number; region: string; weight: number }[]) =>
    request<WebsiteBlock[]>("/website/blocks/order", { method: "PUT", body: JSON.stringify(rows) }),
  listMenus: () => request<WebsiteMenu[]>("/website/menus"),
  createMenu: (key: string, title: string) =>
    request<WebsiteMenu>("/website/menus", { method: "POST", body: JSON.stringify({ key, title }) }),
  saveMenu: (key: string, title: string, items: MenuItemData[]) =>
    request<WebsiteMenu>(`/website/menus/${key}`, { method: "PUT", body: JSON.stringify({ title, items }) }),
  deleteMenu: (key: string) => request<void>(`/website/menus/${key}`, { method: "DELETE" }),
  listPages: () => request<WebsitePageSummary[]>("/website/pages"),
  getPage: (id: number) => request<WebsitePage>(`/website/pages/${id}`),
  createPage: (payload: WebsitePageInput) =>
    request<WebsitePage>("/website/pages", { method: "POST", body: JSON.stringify(payload) }),
  updatePage: (id: number, payload: WebsitePageInput) =>
    request<WebsitePage>(`/website/pages/${id}`, { method: "PUT", body: JSON.stringify(payload) }),
  deletePage: (id: number) => request<void>(`/website/pages/${id}`, { method: "DELETE" }),
  uploadDashboardLogo: (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<DashboardLogoInfo>("/dashboard-logo", { method: "POST", body: form });
  },
  removeDashboardLogo: () => request<DashboardLogoInfo>("/dashboard-logo", { method: "DELETE" }),
  restoreDashboardLogo: () => request<DashboardLogoInfo>("/dashboard-logo/restore-default", { method: "POST" }),
  listAssets: () => request<WebsiteAsset[]>("/website/assets"),
  uploadAsset: (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<WebsiteAsset>("/website/assets", { method: "POST", body: form });
  },
  deleteAsset: (id: string) => request<void>(`/website/assets/${id}`, { method: "DELETE" }),
  getSiteContent: () => request<SiteContent>("/website/site"),
  saveSiteContent: (payload: SiteContent) =>
    request<SiteContent>("/website/site", { method: "PUT", body: JSON.stringify(payload) }),
  getSiteStations: () => request<StationVisibility[]>("/website/stations"),
  saveSiteStations: (rows: { id: number; public_visible: boolean; sort_order: number }[]) =>
    request<StationVisibility[]>("/website/stations", { method: "PUT", body: JSON.stringify(rows) }),

  login: (email: string, password: string) => {
    const form = new URLSearchParams();
    form.set("username", email);
    form.set("password", password);
    return request<{ access_token: string; token_type: string }>("/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: form.toString(),
    });
  },

  me: () => request<CurrentUser>("/auth/me"),

  myStations: () => request<StationBrief[]>("/stations/mine"),
  stationOverview: () => request<StationOverview[]>("/stations/overview"),
  listStations: () => request<StationAdmin[]>("/stations"),
  stationAudioStatus: () => request<{ known: boolean; pending: string[] }>("/stations/audio-status"),
  createStation: (payload: StationCreate) =>
    request<StationAdmin>("/stations", { method: "POST", body: JSON.stringify(payload) }),
  editStation: (id: number, payload: StationEdit) =>
    request<StationAdmin>(`/stations/${id}`, { method: "PUT", body: JSON.stringify(payload) }),
  deleteStation: (id: number, confirm: string) =>
    request<void>(`/stations/${id}?confirm=${encodeURIComponent(confirm)}`, { method: "DELETE" }),

  listUsers: () => request<UserAccount[]>("/users"),

  createUser: (payload: UserCreate) =>
    request<UserAccount>("/users", { method: "POST", body: JSON.stringify(payload) }),

  updateUser: (id: string, payload: UserUpdate) =>
    request<UserAccount>(`/users/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),

  deleteUser: (id: string) => request<void>(`/users/${id}`, { method: "DELETE" }),

  listRoles: () => request<RoleInfo[]>("/roles"),

  listPermissionAreas: () => request<PermissionArea[]>("/roles/permissions"),

  createRole: (payload: RoleCreate) =>
    request<RoleInfo>("/roles", { method: "POST", body: JSON.stringify(payload) }),

  updateRole: (id: string, payload: RoleUpdate) =>
    request<RoleInfo>(`/roles/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),

  deleteRole: (id: string) => request<void>(`/roles/${id}`, { method: "DELETE" }),

  listTracks: () => request<Track[]>("/tracks"),

  uploadTrack: (file: File, category = "music") => {
    const form = new FormData();
    form.set("file", file);
    return request<Track>(`/tracks/upload?category=${encodeURIComponent(category)}`, { method: "POST", body: form });
  },
  updateTrack: (
    id: string,
    payload: { category?: string; title?: string; artist?: string; album?: string | null; year?: number | null; genre?: string | null }
  ) => request<Track>(`/tracks/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  replaceTrackCover: (id: string, file: File) => {
    const form = new FormData();
    form.set("file", file);
    return request<Track>(`/tracks/${id}/cover`, { method: "POST", body: form });
  },
  rotation: () => request<RotationOverview>("/rotation"),
  createRotationRule: (payload: { name: string; category: string; mode: string; every_n: number }) =>
    request<RotationOverview>("/rotation", { method: "POST", body: JSON.stringify(payload) }),
  updateRotationRule: (id: string, payload: { name?: string; mode?: string; every_n?: number; enabled?: boolean }) =>
    request<RotationOverview>(`/rotation/${id}`, { method: "PUT", body: JSON.stringify(payload) }),
  deleteRotationRule: (id: string) => request<RotationOverview>(`/rotation/${id}`, { method: "DELETE" }),

  deleteTrack: (id: string) => request<void>(`/tracks/${id}`, { method: "DELETE" }),

  nowPlaying: () => request<NowPlaying>("/playout/now-playing"),

  upNext: (limit = 5) => request<UpNextTrack[]>(`/playout/up-next?limit=${limit}`),

  getStationConfig: () => request<StationConfig>("/station/config"),

  updateStationConfig: (payload: StationConfigUpdate) =>
    request<StationConfig>("/station/config", {
      method: "PUT",
      body: JSON.stringify(payload),
    }),

  manualPages: () => request<ManualSummary[]>("/manual"),
  manualPage: (slug: string) => request<ManualPage>(`/manual/${encodeURIComponent(slug)}`),
  manualSearch: (q: string) => request<ManualHit[]>(`/manual/search?q=${encodeURIComponent(q)}`),
  appSettings: () => request<AppSettingsState>("/app-settings"),
  saveAppSettings: (payload: AppSettings & { stations: Omit<AppStationRow, "slug" | "name" | "public_visible">[] }) =>
    request<AppSettingsState>("/app-settings", { method: "PUT", body: JSON.stringify(payload) }),
  geoRules: () => request<GeoState>("/station/geo"),
  geoCountries: () => request<{ code: string; name: string }[]>("/station/geo/countries"),
  saveGeoRules: (rules: GeoRules) =>
    request<GeoState>("/station/geo", { method: "PUT", body: JSON.stringify(rules) }),
  testGeo: (ip: string) =>
    request<GeoTestResult>("/station/geo/test", { method: "POST", body: JSON.stringify({ ip }) }),
  updateStationTweaks: (payload: StationTweaks) =>
    request<StationConfig>("/station/tweaks", { method: "PUT", body: JSON.stringify(payload) }),

  getStationStatus: () => request<StationStatus>("/station/status"),

  getInfraConfig: () => request<InfraConfig>("/station/infra"),

  liveStatus: () => request<LiveStatus>("/live/status"),
  createDj: (payload: { username: string; display_name: string; password: string }) =>
    request<LiveStatus>("/live/accounts", { method: "POST", body: JSON.stringify(payload) }),
  updateDj: (id: string, payload: { display_name?: string; password?: string; enabled?: boolean }) =>
    request<LiveStatus>(`/live/accounts/${id}`, { method: "PUT", body: JSON.stringify(payload) }),
  deleteDj: (id: string) => request<LiveStatus>(`/live/accounts/${id}`, { method: "DELETE" }),
  disconnectDj: () => request<LiveStatus>("/live/disconnect", { method: "POST" }),
  streamProfiles: () => request<StreamProfiles>("/station/stream-profiles"),
  createStreamProfile: (payload: { name: string; codec: string; bitrate_kbps: number }) =>
    request<StreamProfiles>("/station/stream-profiles", { method: "POST", body: JSON.stringify(payload) }),
  updateStreamProfile: (id: string, payload: { name?: string; enabled?: boolean }) =>
    request<StreamProfiles>(`/station/stream-profiles/${id}`, { method: "PUT", body: JSON.stringify(payload) }),
  deleteStreamProfile: (id: string) =>
    request<StreamProfiles>(`/station/stream-profiles/${id}`, { method: "DELETE" }),

  listPlaylists: () => request<PlaylistSummary[]>("/playlists"),

  createPlaylist: (name: string, description: string | null, smart_criteria?: SmartCriteria) =>
    request<Playlist>("/playlists", {
      method: "POST",
      body: JSON.stringify({ name, description, ...(smart_criteria ? { smart_criteria } : {}) }),
    }),

  smartOptions: () => request<SmartOptions>("/playlists/smart/options"),
  smartPreview: (criteria: SmartCriteria) =>
    request<SmartPreview>("/playlists/smart/preview", { method: "POST", body: JSON.stringify(criteria) }),
  updateSmartRules: (id: string, criteria: SmartCriteria) =>
    request<Playlist>(`/playlists/${id}/smart`, { method: "PUT", body: JSON.stringify(criteria) }),

  getPlaylist: (id: string) => request<Playlist>(`/playlists/${id}`),

  updatePlaylist: (id: string, name: string, description: string | null) =>
    request<Playlist>(`/playlists/${id}`, {
      method: "PUT",
      body: JSON.stringify({ name, description }),
    }),

  updatePlaylistSettings: (id: string, settings: PlaylistSettings) =>
    request<Playlist>(`/playlists/${id}/settings`, {
      method: "PUT",
      body: JSON.stringify(settings),
    }),

  deletePlaylist: (id: string) => request<void>(`/playlists/${id}`, { method: "DELETE" }),

  activatePlaylist: (id: string) =>
    request<Playlist>(`/playlists/${id}/activate`, { method: "POST" }),

  deactivatePlaylist: (id: string) =>
    request<Playlist>(`/playlists/${id}/deactivate`, { method: "POST" }),

  addTrackToPlaylist: (playlistId: string, trackId: string) =>
    request<Playlist>(`/playlists/${playlistId}/tracks`, {
      method: "POST",
      body: JSON.stringify({ track_id: trackId }),
    }),

  addTracksToPlaylist: (playlistId: string, trackIds: string[]) =>
    request<Playlist>(`/playlists/${playlistId}/tracks/bulk`, {
      method: "POST",
      body: JSON.stringify({ track_ids: trackIds }),
    }),

  clearPlaylist: (playlistId: string) =>
    request<Playlist>(`/playlists/${playlistId}/tracks`, { method: "DELETE" }),

  removeTrackFromPlaylist: (playlistId: string, playlistTrackId: string) =>
    request<Playlist>(`/playlists/${playlistId}/tracks/${playlistTrackId}`, {
      method: "DELETE",
    }),

  reorderPlaylist: (playlistId: string, playlistTrackIds: string[]) =>
    request<Playlist>(`/playlists/${playlistId}/reorder`, {
      method: "PUT",
      body: JSON.stringify({ playlist_track_ids: playlistTrackIds }),
    }),

  getScheduleTimezone: () => request<{ timezone: string }>("/schedule/timezone"),

  setScheduleTimezone: (timezone: string) =>
    request<{ timezone: string }>("/schedule/timezone", {
      method: "PUT",
      body: JSON.stringify({ timezone }),
    }),

  listShows: () => request<Show[]>("/schedule/shows"),

  createShow: (input: ShowInput) =>
    request<Show>("/schedule/shows", { method: "POST", body: JSON.stringify(input) }),

  updateShow: (id: string, input: ShowInput) =>
    request<Show>(`/schedule/shows/${id}`, { method: "PUT", body: JSON.stringify(input) }),

  deleteShow: (id: string) => request<void>(`/schedule/shows/${id}`, { method: "DELETE" }),

  cancelShowOccurrence: (id: string, startLocal: string) =>
    request<Show>(`/schedule/shows/${id}/cancel`, {
      method: "POST",
      body: JSON.stringify({ start_local: startLocal }),
    }),

  listOccurrences: (start: string, end: string) =>
    request<ShowOccurrence[]>(
      `/schedule/occurrences?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
    ),

  scheduleNow: () => request<ScheduleNowNext>("/schedule/now"),

  listServices: () => request<ServiceStatus[]>("/services"),
  serviceOverview: () => request<ServiceOverview[]>("/services/overview"),

  stats: (minutes = 60) => request<StatsResponse>(`/stats?minutes=${minutes}`),
  activity: (opts: { limit?: number; before?: string | null; category?: string | null; q?: string } = {}) => {
    const params = new URLSearchParams({ limit: String(opts.limit ?? 20) });
    if (opts.before) params.set("before", opts.before);
    if (opts.category) params.set("category", opts.category);
    if (opts.q?.trim()) params.set("q", opts.q.trim());
    return request<ActivityResponse>(`/activity?${params}`);
  },
  requestsList: (opts: { status?: string | null; q?: string; before?: string | null; limit?: number } = {}) => {
    const params = new URLSearchParams({ limit: String(opts.limit ?? 25) });
    if (opts.status) params.set("status", opts.status);
    if (opts.q?.trim()) params.set("q", opts.q.trim());
    if (opts.before) params.set("before", opts.before);
    return request<RequestList>(`/requests?${params}`);
  },
  requestsSummary: () => request<RequestSummary>("/requests/summary"),
  requestsInsights: (days = 7) => request<RequestInsights>(`/requests/insights?days=${days}`),
  requestRules: () => request<RequestRules>("/requests/rules"),
  saveRequestRules: (rules: RequestRules) =>
    request<RequestRules>("/requests/rules", { method: "PUT", body: JSON.stringify(rules) }),
  approveRequest: (id: string) => request<SongRequest>(`/requests/${id}/approve`, { method: "POST" }),
  playRequestNext: (id: string) => request<SongRequest>(`/requests/${id}/play-next`, { method: "POST" }),
  queueTrack: (trackId: string) =>
    request<SongRequest>("/requests/queue", { method: "POST", body: JSON.stringify({ track_id: trackId }) }),
  pushConfig: () => request<{ public_key: string }>("/push/config"),
  pushState: (endpoint: string) =>
    request<PushState>("/push/state", { method: "POST", body: JSON.stringify({ endpoint }) }),
  pushSubscribe: (sub: { endpoint: string; keys: { p256dh: string; auth: string } }, prefs: PushPrefs) =>
    request<PushState>("/push/subscribe", { method: "POST", body: JSON.stringify({ ...sub, ...prefs }) }),
  pushPreferences: (endpoint: string, prefs: PushPrefs) =>
    request<PushState>("/push/preferences", { method: "PUT", body: JSON.stringify({ endpoint, ...prefs }) }),
  pushUnsubscribe: (endpoint: string) =>
    request<PushState>("/push/unsubscribe", { method: "POST", body: JSON.stringify({ endpoint }) }),
  pushTest: (endpoint: string) =>
    request<PushState>("/push/test", { method: "POST", body: JSON.stringify({ endpoint }) }),
  rejectRequest: (id: string, reason?: string) =>
    request<SongRequest>(`/requests/${id}/reject`, { method: "POST", body: JSON.stringify({ reason: reason ?? null }) }),
  requestConfig: () => request<RequestPublicConfig>("/requests/public/config"),
  searchRequestable: (q: string) =>
    request<RequestSearchResult[]>(`/requests/public/search?q=${encodeURIComponent(q)}`),
  submitRequest: (body: { track_id: string; name?: string; message?: string }) =>
    request<RequestSubmitted>("/requests/public", { method: "POST", body: JSON.stringify(body) }),
  recentPlays: (limit = 10) => request<RecentPlay[]>(`/playout/recent?limit=${limit}`),
  statsAudience: (days = 7) => request<AudienceStats>(`/stats/audience?days=${days}`),
  liveListeners: () => request<LiveListener[]>("/stats/listeners"),
  statsToday: () => request<TodayStats>("/stats/today"),
  statsHistory: (days = 7) => request<StatsHistory>(`/stats/history?days=${days}`),
  trackStats: (days = 7, limit = 5) => request<TrackStats>(`/stats/tracks?days=${days}&limit=${limit}`),
  storageHistory: (days = 7) => request<StorageHistory>(`/stats/storage?days=${days}`),

  restartService: (name: string) =>
    request<ServiceRestartResult>(`/services/${name}/restart`, { method: "POST" }),

  health: () => request<{ status: string; database: string }>("/health"),
};
